import { toValue, type MaybeRefOrGetter, type UseIntersectionObserverOptions } from '@vueuse/core';
import Vue, { onMounted } from 'vue';

import VueObserveVisibility from 'vue-observe-visibility';

import type { ImpressionTrackingEvents } from '@/enums/events';
import type { TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

Vue.use(VueObserveVisibility);

export interface VueObserveVisibilityOptions {
	callback?: (isVisible: boolean, entry: IntersectionObserverEntry) => void;
	once?: boolean;
	throttle?: number;
	throttleOptions?: {
		leading?: 'visible' | 'hidden' | 'both';
	};
	intersection?: UseIntersectionObserverOptions;
}

export interface VisibilityChangedPayload<T> {
	element: T;
	isVisible: boolean;
}

export type OnIntersectionCallback<T> = (payload: VisibilityChangedPayload<T>) => void;

/**
 * According to MRC, a display ad will be considered “viewable”
 * if 50% of the ad creative is visible for at least one
 * second in the viewable space of the browser.
 */
export const mrcObservableOptions: VueObserveVisibilityOptions = {
	once: false,
	throttle: 1000,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.5, // 50% visibility of each element
	},
} as const;

const properties: TrackingProviderPropertiesInterface = { action: 'impression', nonInteraction: true } as const;

/** Options to initialise the generic batch impression tracker for a category. */
interface UseBatchImpressionTrackingOptions<T> {
	/**
	 * Tracking category for the impression events.
	 * Contexts will be grouped according to this.
	 */
	category: ImpressionTrackingEvents;

	/**
	 * Transform the impressed elements into one or more tracking contexts.
	 * Supports two-dimensional arrays if you want 2+ contexts per element.
	 */
	toContext: (element: T) => SnowplowContext | SnowplowContext[];
}

/** Options for category specific impression tracking composables.  */
export interface BatchImpressionTrackingOptions {
	/** Manage visibility intersection, thresholds, and more. */
	observableOptions?: VueObserveVisibilityOptions;

	/**
	 * How often (in ms) to send the batched impression events.
	 * @default 1000
	 */
	trackingInterval?: MaybeRefOrGetter<number>;

	/** Extra contexts to include in the sent impression event. */
	additionalContexts?: MaybeRefOrGetter<SnowplowContext[]>;
}

type ElementContexts<T> = Map<T, SnowplowContext | SnowplowContext[]>;
type ImpressionCategories<T = any> = Record<ImpressionTrackingEvents, ElementContexts<T>>;
const impressionCategories: ImpressionCategories = {
	ad: new Map(),
	module: new Map(),
	offer: new Map(),
	similartitles: new Map(),
	sponsored_recommendations: new Map(),
	title: new Map(),
} as const;

let timer: NodeJS.Timeout;

/**
 * Track a large number of impression events together.
 * Avoid using this directly, instead use it to create impression
 * tracking composables for specific kinds of elements.
 */
export function useBatchImpressionTracking<T>({
	category,
	toContext,
	observableOptions = mrcObservableOptions,
	trackingInterval = 1000,
	additionalContexts = [],
}: UseBatchImpressionTrackingOptions<T> & BatchImpressionTrackingOptions) {
	// SETUP
	onMounted(() => {
		if (timer) return;

		// track impressions before tab/browser is closed
		if ('onbeforeunload' in window) {
			window.addEventListener('beforeunload', sendImpressionEvent);
		}

		timer = setInterval(sendImpressionEvent, toValue(trackingInterval));
	});

	/** Sends the single impression event with the batched contexts for each category. */
	async function sendImpressionEvent() {
		for (const category in impressionCategories) {
			await sendCategoryImpressionEvent(category as ImpressionTrackingEvents);
		}
	}

	/** Sends the impression event with the batched contexts for the given category. */
	async function sendCategoryImpressionEvent(category: ImpressionTrackingEvents) {
		const elements = impressionCategories[category as ImpressionTrackingEvents];
		if (elements.size === 0) return;

		const contexts = [...elements.values()].flat().concat(toValue(additionalContexts));
		await TrackingHelper.trackEvent(category, properties, contexts);

		elements.clear();
	}

	/**
	 * Completely stop sending impression events.
	 *
	 * @param flush If true, sends a last event with the already batched impressions before stopping.
	 */
	function stopTrackingImpressions(flush = true) {
		if (flush) sendImpressionEvent();
		clearInterval(timer);
	}

	const elements = impressionCategories[category];

	function onIntersection(element: T, isVisible: boolean) {
		if (isVisible && !elements.has(element)) elements.set(element, toContext(element));
	}

	/** Single property if you don't need to override options. */
	function onVisibilityChanged(element: T): VueObserveVisibilityOptions {
		return {
			...observableOptions,
			callback: (isVisible: boolean) => onIntersection(element, isVisible),
		};
	}

	function clearQueue() {
		elements.clear();
	}

	return {
		/** Single property if you don't need to override options. */
		onVisibilityChanged,

		/** The exact callback called whenever visibility changes. */
		onIntersection,

		/** Completely stop sending impression events. */
		stopTrackingImpressions,

		/** Directly send the impression event with the batched impressed elements for this category. */
		sendCategoryImpressionEvent: (category: ImpressionTrackingEvents) => sendCategoryImpressionEvent(category),

		/** Directly send the impression event for all batched categories. */
		sendImpressionEvent,

		/** Clear the currently queued impressed elements without sending an impression event. */
		clearQueue,
	};
}
